import {
  InterpolationParameters,
  TranslateDefaultParser,
  getValue as ngxGetValue,
} from '@ngx-translate/core';
import { isEmpty, get, isNil, isEqual } from 'lodash';

import { generateMessage } from '@app/text-content/messages';

import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';

@Injectable()
export class DefaultTranslationParser extends TranslateDefaultParser {
  /**
   * Override the interpolate method to handle default translations
    * with custom interpolation logic.
    *
    * Note:
    * - This path uses generateMessage for normal string interpolation.
    * - It does NOT apply ENABLE_SPECIAL_TRANSLATION_SYMBOL wrapping.
    *   Symbol wrapping is only applied in getInterpolationReplacement(),
    *   which is used by ngx-translate's missing-translation interpolation flow.
   *
   * @param translation
   * @param params
   * @return interpolated translation string
   */
  public interpolate(
    translation: string | ((...args: any[]) => string),
    params: Record<string, any>,
  ): string | undefined {
    if (isEmpty(translation) || typeof translation !== 'string') {
      return super.interpolate(translation as any, params);
    }

    // Custom logic to handle default translations
    // use the generateMessage function to interpolate the translation
    return generateMessage(translation, params);
  }

  /**
    * Returns the replacement for an interpolation parameter.
    *
    * This hook is used by ngx-translate during interpolation replacement.
    * We keep v17-compatible nested-key lookup via ngxGetValue and apply
    * optional special-symbol wrapping (#...#) in this flow only.
   */
  protected getInterpolationReplacement(
    params: InterpolationParameters,
    key: string,
  ): string | undefined {
    // Custom logic for translation can be added here
    let targetStr: any = key;

    try {
      targetStr = isEmpty(key) ? key : ngxGetValue(params, key);
    } catch (error) {
      console.error('Translation error when get value:', error);
      return targetStr;
    }

    // Nil targetStr should propagate to trigger the missing-translation handler
    if (isNil(targetStr)) {
      return targetStr;
    }

    const isEnableSpecialTranslationSymbol = isEqual(
      get(environment, 'ENABLE_SPECIAL_TRANSLATION_SYMBOL', 'false'),
      'true',
    );

    const translatedStr = isEnableSpecialTranslationSymbol
      ? `#${targetStr}#`
      : targetStr;

    return this.formatValue(translatedStr);
  }
}
